/**
 * UI-8e Task 3 — useFeatureFlags hook.
 *
 * Drives the six platform feature-flag endpoints:
 *
 *   GET    /platform/feature-flags
 *   POST   /platform/feature-flags
 *   PUT    /platform/feature-flags/:flagKey/global
 *   PUT    /platform/feature-flags/:flagKey/override/:tenantId
 *   DELETE /platform/feature-flags/:flagKey/override/:tenantId
 *   DELETE /platform/feature-flags/:flagKey
 *
 * Not built on `useAdminResource` because the list endpoint returns a custom
 * envelope (`{ flags: GroupedFlag[] }` where each entry is a grouped
 * global+overrides record, not a flat CRUD row). The pattern mirrors
 * `useKeyRotation` (UI-8c) — a `useEffect + api.get` load and direct
 * `api.post/put/delete` mutations followed by a refetch so the accordion
 * picks up the new state immediately.
 *
 * All routes are gated server-side by `PLATFORM_FEATURE_FLAGS_ADMIN`; the
 * page (FeatureFlagsPage) wraps itself in <RequirePermission> for the
 * client gate.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface FlagRow {
  id: string
  tenantId: string | null
  flagKey: string
  enabled: boolean
  rolloutPct: number
  updatedBy: string | null
  updatedAt: string
}

export interface OverrideRow extends FlagRow {
  tenantSlug: string
}

export interface GroupedFlag {
  flagKey: string
  global: FlagRow | null
  overrides: OverrideRow[]
}

export interface FlagValue {
  enabled: boolean
  rolloutPct: number
}

export interface NewFlagPayload extends FlagValue {
  flagKey: string
}

export interface UseFeatureFlagsResult {
  flags: GroupedFlag[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  /** POST /platform/feature-flags — creates the global row for a brand-new flag. */
  createGlobal: (payload: NewFlagPayload) => Promise<void>
  /** PUT /platform/feature-flags/:flagKey/global — upserts the global row. */
  setGlobal: (flagKey: string, payload: FlagValue) => Promise<void>
  /** PUT /platform/feature-flags/:flagKey/override/:tenantId — upserts an override. */
  setOverride: (
    flagKey: string,
    tenantId: string,
    payload: FlagValue,
  ) => Promise<void>
  /** DELETE /platform/feature-flags/:flagKey/override/:tenantId — removes one override. */
  removeOverride: (flagKey: string, tenantId: string) => Promise<void>
  /** DELETE /platform/feature-flags/:flagKey — removes global + all overrides. */
  removeFlag: (flagKey: string) => Promise<void>
}

const BASE_PATH = '/platform/feature-flags'

export function useFeatureFlags(): UseFeatureFlagsResult {
  const [flags, setFlags] = useState<GroupedFlag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ flags: GroupedFlag[] }>(BASE_PATH)
      setFlags(res.flags ?? [])
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Failed to load feature flags',
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const createGlobal = useCallback(
    async (payload: NewFlagPayload): Promise<void> => {
      await api.post(BASE_PATH, payload)
      await refetch()
    },
    [refetch],
  )

  const setGlobal = useCallback(
    async (flagKey: string, payload: FlagValue): Promise<void> => {
      await api.put(`${BASE_PATH}/${flagKey}/global`, payload)
      await refetch()
    },
    [refetch],
  )

  const setOverride = useCallback(
    async (
      flagKey: string,
      tenantId: string,
      payload: FlagValue,
    ): Promise<void> => {
      await api.put(
        `${BASE_PATH}/${flagKey}/override/${tenantId}`,
        payload,
      )
      await refetch()
    },
    [refetch],
  )

  const removeOverride = useCallback(
    async (flagKey: string, tenantId: string): Promise<void> => {
      await api.delete(`${BASE_PATH}/${flagKey}/override/${tenantId}`)
      await refetch()
    },
    [refetch],
  )

  const removeFlag = useCallback(
    async (flagKey: string): Promise<void> => {
      await api.delete(`${BASE_PATH}/${flagKey}`)
      await refetch()
    },
    [refetch],
  )

  return {
    flags,
    loading,
    error,
    refetch,
    createGlobal,
    setGlobal,
    setOverride,
    removeOverride,
    removeFlag,
  }
}
